import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getLeads } from '../api/client';
import client from '../api/client';
import { Skeleton } from '../components/Layout';
import { C, Btn, Input, Card, Toast, initials, useNotify } from '../utils/styles';
import CampaignWizard from '../components/CampaignWizard';

const PROVIDERS_META = [
  { id:'custom_smtp', label:'Custom SMTP',         icon:'ti-settings-cog',  color:'#374151' },
  { id:'gmail',       label:'Gmail / Workspace',   icon:'ti-brand-google',  color:'#EA4335' },
  { id:'outlook',     label:'Outlook / Microsoft', icon:'ti-brand-windows', color:'#0078D4' },
  { id:'mailgun',     label:'Mailgun',              icon:'ti-mail-forward',  color:'#F06B66' },
  { id:'sendgrid',    label:'SendGrid',             icon:'ti-send',          color:'#1A82E2' },
];
const DEFAULT_TEMPLATES = [
  { name:'Welcome Email',      subject:'Welcome, {{name}}!',             category:'onboarding', body_html:'<p>Hi {{name}},</p><p>Welcome! We\'re thrilled to have you on board.</p><p>Warm regards,<br/>The Team</p>' },
  { name:'Follow Up',          subject:'Following up — {{name}}',        category:'follow-up',  body_html:'<p>Hi {{name}},</p><p>I wanted to quickly follow up. Any questions?</p><p>Best,<br/>The Team</p>' },
  { name:'Monthly Newsletter', subject:'Monthly Update',                 category:'newsletter', body_html:'<h2 style="color:#0099FF;">Monthly Update 📬</h2><p>Hi {{name}},</p><p>Here\'s what\'s happening this month...</p><p>Cheers,<br/>The Team</p>' },
  { name:'Re-engagement',      subject:'We miss you, {{name}} 👋',       category:'re-engage',  body_html:'<p>Hi {{name}},</p><p>It\'s been a while! We\'d love to reconnect.</p><p>Best,<br/>The Team</p>' },
  { name:'Proposal Follow Up', subject:'Your proposal — any questions?', category:'sales',      body_html:'<p>Hi {{name}},</p><p>I\'m reaching out about the proposal we sent. Happy to help.</p><p>Best,<br/>The Team</p>' },
];
const TABS = [
  { id:'setup',     label:'Setup',     icon:'ti-mail-cog'  },
  { id:'campaigns', label:'Campaigns', icon:'ti-send'      },
  { id:'templates', label:'Templates', icon:'ti-file-text' },
  { id:'analytics', label:'Analytics', icon:'ti-chart-bar' },
];
const SETUP_SECTIONS = [
  { id:'domains',   label:'Sending Domains', icon:'ti-world'   },
  { id:'accounts',  label:'Email Accounts',  icon:'ti-mail-cog'},
  { id:'signature', label:'Signature',       icon:'ti-writing' },
];
const pct = (n, total) => { const t=Math.max(total||0,n||0); return t>0?Math.round(((n||0)/t)*100):0; };

// ── Skeletons ─────────────────────────────────────────────────
const DomainSkeleton = () => (
  <div style={{background:'#fff',borderRadius:12,border:`1px solid ${C.border}`,padding:'16px 18px',marginBottom:12,display:'flex',alignItems:'center',gap:12}}>
    <Skeleton circle size={36}/><div style={{flex:1}}><Skeleton height={13} width="40%" style={{marginBottom:6}}/><Skeleton height={11} width="25%"/></div>
    <Skeleton height={26} width={90} radius={20}/><Skeleton height={26} width={26} radius={6}/>
  </div>
);
const ProviderSkeleton = () => (
  <div style={{background:'#fff',borderRadius:12,border:`1px solid ${C.border}`,padding:18,marginBottom:10,display:'flex',alignItems:'center',gap:14}}>
    <Skeleton circle size={32}/><div style={{flex:1}}><Skeleton height={13} width="50%" style={{marginBottom:6}}/><Skeleton height={11} width="35%"/></div>
    <Skeleton height={30} width={60} radius={6}/><Skeleton height={30} width={80} radius={6}/>
  </div>
);
const CampaignSkeleton = () => (
  <div style={{display:'flex',alignItems:'center',gap:12,padding:'12px 0',borderBottom:`1px solid #f3f4f6`}}>
    <Skeleton circle size={36}/><div style={{flex:1}}><Skeleton height={12} width="40%" style={{marginBottom:5}}/><Skeleton height={10} width="60%"/></div>
    <Skeleton height={22} width={50} radius={20}/><Skeleton height={28} width={28} radius={6}/>
  </div>
);

// ── Rich Editor for Signature only ───────────────────────────
const SigEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  useEffect(() => { if (editorRef.current && editorRef.current.innerHTML !== (value||'')) editorRef.current.innerHTML = value||''; }, []); // eslint-disable-line
  const sync = useCallback(() => { if (editorRef.current) onChange(editorRef.current.innerHTML); }, [onChange]);
  const exec = (cmd, val=null) => { editorRef.current?.focus(); document.execCommand(cmd,false,val); sync(); };
  const bs = (a) => ({ padding:'4px 8px',borderRadius:6,border:'none',cursor:'pointer',fontSize:12,background:a?C.primary:'#f3f4f6',color:a?'#fff':C.dark });
  return (
    <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:'hidden'}}>
      <div style={{display:'flex',gap:2,padding:'5px 8px',background:'#f9fafb',borderBottom:`1px solid ${C.border}`,alignItems:'center'}}>
        <button style={bs(false)} onClick={()=>exec('bold')}><i className="ti ti-bold"/></button>
        <button style={bs(false)} onClick={()=>exec('italic')}><i className="ti ti-italic"/></button>
        <button style={bs(false)} onClick={()=>exec('underline')}><i className="ti ti-underline"/></button>
        <button style={bs(false)} onClick={()=>{const u=window.prompt('URL:','https://');if(u)exec('createLink',u);}}><i className="ti ti-link"/></button>
        <input type="color" onChange={e=>exec('foreColor',e.target.value)} style={{width:22,height:22,borderRadius:3,border:`1px solid ${C.border}`,cursor:'pointer',padding:1,marginLeft:4}}/>
      </div>
      <div ref={editorRef} id="sig-editor" contentEditable suppressContentEditableWarning onInput={sync}
        style={{minHeight:100,padding:'10px 14px',outline:'none',fontSize:13,lineHeight:1.7,color:C.dark}}/>
    </div>
  );
};

// ── Add Domain Modal ─────────────────────────────────────────
const AddDomainModal = ({ onClose, onAdd }) => {
  const [newDomain, setNewDomain] = useState('');
  const [saving,    setSaving]    = useState(false);
  const handleAdd = async () => {
    if (!newDomain.trim()) return;
    setSaving(true);
    try { await onAdd(newDomain.trim()); onClose(); }
    catch { setSaving(false); }
  };
  return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:3000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:16,width:480,padding:28,boxShadow:'0 25px 60px rgba(0,0,0,0.2)'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20}}>
          <div style={{fontWeight:700,fontSize:16,color:'#111827',display:'flex',alignItems:'center',gap:8}}>
            <i className="ti ti-world" style={{color:C.primary,fontSize:18}}/> Add Sending Domain
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',fontSize:20,color:'#9ca3af'}}><i className="ti ti-x"/></button>
        </div>
        <div style={{background:'#f0f9ff',border:'1px solid #bae6fd',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:12,color:'#0369a1'}}>
          <strong>Important:</strong> Your From Email domain must match this sending domain for DMARC alignment. e.g. domain = <code>mail.company.com</code>, from email = <code>you@mail.company.com</code>.
        </div>
        <div style={{marginBottom:16}}>
          <label style={{fontSize:12,fontWeight:500,color:'#374151',marginBottom:4,display:'block'}}>Sending Domain</label>
          <input autoFocus placeholder="mail.yourdomain.com" value={newDomain} onChange={e=>setNewDomain(e.target.value)}
            onKeyDown={e=>e.key==='Enter'&&handleAdd()}
            style={{width:'100%',padding:'9px 12px',borderRadius:8,border:'1px solid #e5e7eb',fontSize:13,outline:'none',boxSizing:'border-box'}}/>
          <div style={{fontSize:11,color:'#9ca3af',marginTop:4}}>DNS propagation takes 24–48 hours after adding records.</div>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button onClick={handleAdd} disabled={saving||!newDomain.trim()}
            style={{padding:'9px 18px',borderRadius:8,border:'none',background:C.primary,color:'#fff',fontWeight:600,fontSize:13,cursor:'pointer',display:'flex',alignItems:'center',gap:6,opacity:saving||!newDomain.trim()?0.6:1}}>
            <i className={`ti ${saving?'ti-loader ti-spin':'ti-plus'}`}/>{saving?'Adding...':'Add & Get DNS Records'}
          </button>
          <button onClick={onClose} style={{padding:'9px 18px',borderRadius:8,border:'1px solid #e5e7eb',background:'#fff',color:'#374151',fontSize:13,cursor:'pointer'}}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ── Add Account Modal ─────────────────────────────────────────
const AddAccountModal = ({ onClose, onSaved, providers }) => {
  const [acc,      setAcc]      = useState({ id:'custom_smtp', host:'', port:'587', username:'', password:'', from_email:'', from_name:'' });
  const [showPass, setShowPass] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const inp = {width:'100%',padding:'8px 12px',borderRadius:8,border:'1px solid #e5e7eb',fontSize:13,outline:'none',boxSizing:'border-box'};
  const handleSave = async () => {
    if (!acc.username && !acc.from_email) return;
    setSaving(true);
    try {
      await client.post('/api/email/provider', { provider:acc.id, host:acc.host, port:acc.port, username:acc.username, password:acc.password, from_email:acc.from_email||acc.username, from_name:acc.from_name, set_as_default:providers.length===0 });
      onSaved(); onClose();
    } finally { setSaving(false); }
  };
  return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:3000,display:'flex',alignItems:'center',justifyContent:'center',padding:20,overflowY:'auto'}}>
      <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:16,width:560,padding:28,boxShadow:'0 25px 60px rgba(0,0,0,0.2)',margin:'20px 0'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20}}>
          <div style={{fontWeight:700,fontSize:16,color:'#111827',display:'flex',alignItems:'center',gap:8}}><i className="ti ti-mail-cog" style={{color:C.primary,fontSize:18}}/> Add Email Account</div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',fontSize:20,color:'#9ca3af'}}><i className="ti ti-x"/></button>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:16}}>
          {PROVIDERS_META.map(p=>(
            <button key={p.id} onClick={()=>setAcc(a=>({...a,id:p.id}))}
              style={{padding:10,borderRadius:10,border:`1px solid ${acc.id===p.id?C.primary:'#e5e7eb'}`,background:acc.id===p.id?'var(--color-primary-light)':'#fff',cursor:'pointer',display:'flex',alignItems:'center',gap:8,textAlign:'left'}}>
              <i className={`ti ${p.icon}`} style={{fontSize:18,color:acc.id===p.id?C.primary:p.color,flexShrink:0}}/>
              <div><div style={{fontWeight:600,fontSize:12,color:acc.id===p.id?C.primary:'#111827'}}>{p.label}</div></div>
              {acc.id===p.id&&<i className="ti ti-circle-check" style={{marginLeft:'auto',fontSize:14,color:C.primary,flexShrink:0}}/>}
            </button>
          ))}
        </div>
        {acc.id==='custom_smtp'&&<div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:8,padding:'8px 12px',fontSize:12,color:'#92400e',marginBottom:12}}><i className="ti ti-alert-triangle" style={{marginRight:4}}/>Use <strong>port 587 (TLS)</strong> — port 465 is blocked on Vercel.</div>}
        {(acc.id==='gmail'||acc.id==='outlook')&&<div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:8,padding:'8px 12px',fontSize:12,color:'#92400e',marginBottom:12}}><i className="ti ti-info-circle" style={{marginRight:4}}/>Use an <strong>App Password</strong>, not your regular password.</div>}
        <div style={{display:'grid',gap:10,marginBottom:16}}>
          {acc.id==='custom_smtp'&&(
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
              <div><label style={{fontSize:12,fontWeight:500,color:'#374151',marginBottom:4,display:'block'}}>SMTP Host</label><input style={inp} placeholder="smtp.yourdomain.com" value={acc.host} onChange={e=>setAcc(a=>({...a,host:e.target.value}))}/></div>
              <div><label style={{fontSize:12,fontWeight:500,color:'#374151',marginBottom:4,display:'block'}}>Port</label>
                <select style={{...inp,background:'#fff'}} value={acc.port} onChange={e=>setAcc(a=>({...a,port:e.target.value}))}>
                  <option value="587">587 (TLS — Recommended)</option><option value="465">465 (SSL)</option><option value="25">25</option>
                </select>
              </div>
            </div>
          )}
          <div><label style={{fontSize:12,fontWeight:500,color:'#374151',marginBottom:4,display:'block'}}>Email / Username</label><input style={inp} placeholder="you@yourdomain.com" value={acc.username} onChange={e=>setAcc(a=>({...a,username:e.target.value}))}/></div>
          <div><label style={{fontSize:12,fontWeight:500,color:'#374151',marginBottom:4,display:'block'}}>Password / App Password</label>
            <div style={{position:'relative'}}>
              <input type={showPass?'text':'password'} placeholder="••••••••" value={acc.password} onChange={e=>setAcc(a=>({...a,password:e.target.value}))} style={{...inp,paddingRight:38}}/>
              <button type="button" onClick={()=>setShowPass(!showPass)} style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#9ca3af'}}><i className={`ti ${showPass?'ti-eye-off':'ti-eye'}`} style={{fontSize:16}}/></button>
            </div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <div><label style={{fontSize:12,fontWeight:500,color:'#374151',marginBottom:4,display:'block'}}>From Email</label><input style={inp} placeholder="hello@yourdomain.com" value={acc.from_email} onChange={e=>setAcc(a=>({...a,from_email:e.target.value}))}/></div>
            <div><label style={{fontSize:12,fontWeight:500,color:'#374151',marginBottom:4,display:'block'}}>From Name</label><input style={inp} placeholder="Your Company" value={acc.from_name} onChange={e=>setAcc(a=>({...a,from_name:e.target.value}))}/></div>
          </div>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button onClick={handleSave} disabled={saving||(!acc.username&&!acc.from_email)}
            style={{padding:'9px 18px',borderRadius:8,border:'none',background:C.primary,color:'#fff',fontWeight:600,fontSize:13,cursor:'pointer',display:'flex',alignItems:'center',gap:6,opacity:saving?0.7:1}}>
            <i className={`ti ${saving?'ti-loader ti-spin':'ti-device-floppy'}`}/>{saving?'Saving...':'Save Account'}
          </button>
          <button onClick={onClose} style={{padding:'9px 18px',borderRadius:8,border:'1px solid #e5e7eb',background:'#fff',color:'#374151',fontSize:13,cursor:'pointer'}}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ── DNS Record Row ────────────────────────────────────────────
const RecordRow = ({ r, copied, onCopy }) => {
  const statusIcon  = r.status==='verified'?'ti-circle-check':r.status==='failed'?'ti-circle-x':'ti-clock';
  const statusColor = r.status==='verified'?C.success:r.status==='failed'?C.danger:C.warning;
  return (
    <div style={{background:'#fff',borderRadius:10,border:`1px solid ${r.status==='verified'?C.success:r.status==='failed'?C.danger:C.border}`,padding:14,marginBottom:8}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
        <span style={{background:'var(--color-primary-light)',color:C.primary,borderRadius:6,padding:'2px 8px',fontSize:11,fontWeight:700}}>{r.type}</span>
        <span style={{fontWeight:700,fontSize:13,color:C.dark}}>{r.label}</span>
        <span style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:4,color:statusColor,fontSize:12,fontWeight:500}}>
          <i className={`ti ${statusIcon}`} style={{fontSize:15}}/>{r.status==='verified'?'Verified':r.status==='failed'?'Not found':'Pending'}
        </span>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'60px 1fr',gap:8,fontSize:12}}>
        <div style={{color:C.faint,paddingTop:4}}>Name:</div>
        <div style={{display:'flex',alignItems:'center',gap:6}}>
          <code style={{flex:1,background:'#f9fafb',border:`1px solid ${C.border}`,borderRadius:4,padding:'4px 8px',fontSize:11,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.name}</code>
          <button onClick={()=>onCopy(r.name)} style={{padding:'3px 10px',borderRadius:6,border:`1px solid ${C.border}`,background:'#fff',cursor:'pointer',fontSize:11,color:copied===r.name?C.success:C.primary,whiteSpace:'nowrap',flexShrink:0}}>{copied===r.name?'✓':'Copy'}</button>
        </div>
        <div style={{color:C.faint,paddingTop:4}}>Value:</div>
        <div style={{display:'flex',alignItems:'center',gap:6}}>
          <code style={{flex:1,background:'#f9fafb',border:`1px solid ${C.border}`,borderRadius:4,padding:'4px 8px',fontSize:11,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.value}</code>
          <button onClick={()=>onCopy(r.value)} style={{padding:'3px 10px',borderRadius:6,border:`1px solid ${C.border}`,background:'#fff',cursor:'pointer',fontSize:11,color:copied===r.value?C.success:C.primary,whiteSpace:'nowrap',flexShrink:0}}>{copied===r.value?'✓':'Copy'}</button>
        </div>
      </div>
      {r.note&&<div style={{fontSize:11,color:'#92400e',marginTop:8,background:'#fffbeb',borderRadius:6,padding:'6px 10px'}}><i className="ti ti-alert-triangle" style={{marginRight:4}}/>{r.note}</div>}
    </div>
  );
};

const StatBar = ({ label, value, total, color }) => (
  <div style={{marginBottom:12}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
      <span style={{fontSize:12,color:C.muted}}>{label}</span>
      <span style={{fontSize:12,fontWeight:600,color}}>{value} <span style={{color:C.faint}}>({pct(value,total)}%)</span></span>
    </div>
    <div style={{background:'#f3f4f6',borderRadius:4,height:6}}>
      <div style={{width:`${pct(value,total)}%`,height:6,borderRadius:4,background:color,transition:'width 0.4s'}}/>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════
export default function Email() {
  const [activeTab,    setActiveTab]    = useState('setup');
  const [setupSection, setSetupSection] = useState('domains');
  const [notification, notify]          = useNotify();

  const [domains,          setDomains]          = useState([]);
  const [domainsLoading,   setDomainsLoading]   = useState(true);
  const [providers,        setProviders]        = useState([]);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [campaigns,        setCampaigns]        = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [templates,        setTemplates]        = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [leads,            setLeads]            = useState([]);
  const [analytics,        setAnalytics]        = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Signature
  const [sigProviderId, setSigProviderId] = useState('');
  const [signature,     setSignature]     = useState('');
  const [sigLoading,    setSigLoading]    = useState(false);
  const [sigSaving,     setSigSaving]     = useState(false);

  // Modals
  const [showAddDomainModal,  setShowAddDomainModal]  = useState(false);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);

  // Campaign wizard
  const [showCompose,  setShowCompose]  = useState(false);
  const [editCampaign, setEditCampaign] = useState(null);

  // Campaign detail view
  const [viewCampaign,  setViewCampaign]  = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Domain state
  const [expandedDomain, setExpandedDomain] = useState(null);
  const [verifying,      setVerifying]      = useState('');
  const [copied,         setCopied]         = useState('');

  // Account state
  const [testingId, setTestingId] = useState('');

  // Template state
  const [showTemplate, setShowTemplate] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [newTemplate,  setNewTemplate]  = useState({ name:'', subject:'', body_html:'', category:'' });

  // Analytics


  const tabsLoaded = useRef({ setup:false, campaigns:false, templates:false, analytics:false });

  // ── Fetch functions ────────────────────────────────────────
  const fetchDomains = useCallback(async () => {
    setDomainsLoading(true);
    try { const { data } = await client.get('/api/email/domains'); setDomains(data||[]); }
    catch {} finally { setDomainsLoading(false); }
  }, []);
  const fetchProviders = useCallback(async () => {
    setProvidersLoading(true);
    try { const { data } = await client.get('/api/email/providers'); setProviders(data||[]); }
    catch {} finally { setProvidersLoading(false); }
  }, []);
  const fetchCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    try { const { data } = await client.get('/api/email/campaigns'); setCampaigns(data||[]); }
    catch {} finally { setCampaignsLoading(false); }
  }, []);
  const fetchTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try { const { data } = await client.get('/api/email/templates'); setTemplates(data||[]); }
    catch {} finally { setTemplatesLoading(false); }
  }, []);
  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try { const { data } = await client.get('/api/email/analytics'); setAnalytics(data||[]); }
    catch {} finally { setAnalyticsLoading(false); }
  }, []);
  const fetchLeads = useCallback(async () => {
    try { const { data } = await getLeads({ limit:500 }); setLeads(data?.leads||[]); }
    catch {}
  }, []);
  const fetchSignature = useCallback(async (providerId) => {
    setSigLoading(true);
    try {
      const url = providerId ? `/api/email/signature?provider_id=${providerId}` : '/api/email/signature';
      const { data } = await client.get(url);
      setSignature(data?.signature||'');
      if (!providerId && data?.provider_id) setSigProviderId(data.provider_id);
    } catch {} finally { setSigLoading(false); }
  }, []);

  useEffect(() => {
    fetchDomains(); fetchProviders(); fetchLeads();
    tabsLoaded.current.setup = true;
  }, []); // eslint-disable-line

  useEffect(() => {
    if (providers.length > 0 && !sigProviderId) {
      const def = providers.find(p=>p.is_default);
      if (def) { setSigProviderId(def.id); fetchSignature(def.id); }
    }
  }, [providers, sigProviderId, fetchSignature]);

  useEffect(() => {
    if (activeTab==='campaigns' && !tabsLoaded.current.campaigns) { fetchCampaigns(); tabsLoaded.current.campaigns=true; }
    if (activeTab==='templates' && !tabsLoaded.current.templates) { fetchTemplates(); tabsLoaded.current.templates=true; }
    if (activeTab==='analytics' && !tabsLoaded.current.analytics) { fetchAnalytics(); tabsLoaded.current.analytics=true; }
  }, [activeTab, fetchCampaigns, fetchTemplates, fetchAnalytics]);

  // ── Campaign detail ────────────────────────────────────────
  const openCampaignDetail = async (campaign) => {
    setLoadingDetail(true);
    setViewCampaign({ ...campaign, recipients:[] });
    try { const { data } = await client.get(`/api/email/campaigns/${campaign.id}`); setViewCampaign(data); }
    catch {} finally { setLoadingDetail(false); }
  };

  // ── Domain handlers ────────────────────────────────────────
  const handleAddDomain = async (domain) => {
    const { data } = await client.post('/api/email/domains', { domain });
    setDomains(d=>[...d,data]); setExpandedDomain(data.id);
    notify(`Domain ${data.domain} added — add DNS records below`);
  };
  const handleVerifyDomain = async (id) => {
    setVerifying(id);
    try {
      const { data } = await client.post(`/api/email/domains/${id}/verify`);
      setDomains(d=>d.map(dom=>dom.id===id?{...dom,status:data.status,dns_records:data.dns_records}:dom));
      if (data.status==='verified') notify(`✅ ${data.domain} fully verified!`);
      else notify(data.message,'error');
    } catch (err) { notify(err.response?.data?.error||'Failed','error'); }
    finally { setVerifying(''); }
  };
  const handleDeleteDomain = async (id) => {
    if (!window.confirm('Remove this domain?')) return;
    try { await client.delete(`/api/email/domains/${id}`); setDomains(d=>d.filter(x=>x.id!==id)); notify('Domain removed'); }
    catch { notify('Failed','error'); }
  };
  const handleCopy = (val) => { navigator.clipboard.writeText(val); setCopied(val); setTimeout(()=>setCopied(''),2000); };

  // ── Account handlers ───────────────────────────────────────
  const handleTest = async (id) => {
    setTestingId(id);
    try { const { data } = await client.post('/api/email/test',{provider_id:id}); notify(data.message||'Test email sent!'); }
    catch (err) { notify(err.response?.data?.error||'Test failed','error'); }
    finally { setTestingId(''); }
  };
  const handleSetDefault = async (id) => {
    try { await client.patch(`/api/email/providers/${id}/default`); setProviders(p=>p.map(x=>({...x,is_default:x.id===id}))); notify('Default updated'); }
    catch { notify('Failed','error'); }
  };
  const handleDeleteProvider = async (id) => {
    if (!window.confirm('Remove this email account?')) return;
    try { await client.delete(`/api/email/providers/${id}`); setProviders(p=>p.filter(x=>x.id!==id)); notify('Account removed'); }
    catch { notify('Failed','error'); }
  };

  // ── Signature ──────────────────────────────────────────────
  const handleSaveSignature = async () => {
    setSigSaving(true);
    try { await client.post('/api/email/signature', { signature, provider_id:sigProviderId||undefined }); notify('Signature saved!'); }
    catch { notify('Failed to save signature','error'); }
    finally { setSigSaving(false); }
  };

  // ── Campaign compose ───────────────────────────────────────
  const openCompose = () => { setEditCampaign(null); setShowCompose(true); };
  const openEdit    = (c) => { setEditCampaign(c);   setShowCompose(true); };

  const handleDeleteCampaign = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try { await client.delete(`/api/email/campaigns/${id}`); setCampaigns(c=>c.filter(x=>x.id!==id)); notify('Deleted'); }
    catch { notify('Failed','error'); }
  };

  // ── Templates ──────────────────────────────────────────────
  const handleSaveTemplate = async () => {
    if (!newTemplate.name) return notify('Name required','error');
    try {
      const { data } = await client.post('/api/email/templates', newTemplate);
      setTemplates(t=>[data,...t]); setShowTemplate(false);
      setNewTemplate({ name:'', subject:'', body_html:'', category:'' }); notify('Template saved!');
    } catch (err) { notify(err.response?.data?.error||'Failed','error'); }
  };
  const handleUpdateTemplate = async () => {
    if (!editTemplate?.name) return notify('Name required','error');
    try {
      const { data } = await client.patch(`/api/email/templates/${editTemplate.id}`, editTemplate);
      setTemplates(t=>t.map(x=>x.id===editTemplate.id?data:x)); setEditTemplate(null); notify('Updated!');
    } catch (err) { notify(err.response?.data?.error||'Failed','error'); }
  };
  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await client.delete(`/api/email/templates/${id}`); setTemplates(t=>t.filter(x=>x.id!==id)); notify('Deleted'); }
    catch { notify('Failed','error'); }
  };
  const handleUseTemplate = (t) => {
    setEditCampaign({ ...t, id:null, body_html:t.body_html });
    setActiveTab('campaigns'); setShowCompose(true);
    notify(`Template "${t.name}" loaded`);
  };

  const domainStatusColor = (s) => s==='verified'?C.success:s==='partial'?C.warning:'#9ca3af';
  const domainStatusLabel = (s) => s==='verified'?'✓ Fully Verified':s==='partial'?'⚡ Partially Verified':'⏳ Pending';
  const inp = { width:'100%', padding:'8px 12px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13, outline:'none', boxSizing:'border-box' };

  return (
    <div>
      <Toast {...notification}/>

      {/* ── Modals ── */}
      {showAddDomainModal  && <AddDomainModal  onClose={()=>setShowAddDomainModal(false)}  onAdd={handleAddDomain}/>}
      {showAddAccountModal && <AddAccountModal onClose={()=>setShowAddAccountModal(false)} onSaved={()=>{ fetchProviders(); notify('Email account added!'); }} providers={providers}/>}

      {/* ── Campaign Wizard ── */}
      {showCompose && (
        <CampaignWizard
          editCampaign={editCampaign}
          providers={providers}
          leads={leads}
          onClose={()=>{ setShowCompose(false); setEditCampaign(null); }}
          onSent={()=>{ fetchCampaigns(); tabsLoaded.current.campaigns=true; }}
          onDraftSaved={()=>{ fetchCampaigns(); tabsLoaded.current.campaigns=true; }}
          notify={notify}
        />
      )}

      {/* ── Campaign Detail Modal ── */}
      {viewCampaign && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:2000,display:'flex',alignItems:'flex-start',justifyContent:'center',padding:'24px 16px',overflowY:'auto'}} onClick={()=>setViewCampaign(null)}>
          <div style={{background:'#fff',borderRadius:16,width:'100%',maxWidth:760,boxShadow:'0 25px 60px rgba(0,0,0,0.25)',marginBottom:24}} onClick={e=>e.stopPropagation()}>
            <div style={{padding:'20px 24px',borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div>
                <div style={{fontWeight:700,fontSize:16,color:C.dark}}>{viewCampaign.name}</div>
                <div style={{fontSize:12,color:C.muted,marginTop:2}}>{viewCampaign.subject}{viewCampaign.sent_at&&<span> · Sent {new Date(viewCampaign.sent_at).toLocaleDateString('en-IN',{dateStyle:'medium'})}</span>}{viewCampaign.scheduled_at&&viewCampaign.status==='scheduled'&&<span style={{color:C.warning}}> · Scheduled {new Date(viewCampaign.scheduled_at).toLocaleString('en-IN',{dateStyle:'medium',timeStyle:'short'})}</span>}</div>
              </div>
              <button onClick={()=>setViewCampaign(null)} style={{background:'#f3f4f6',border:'none',borderRadius:8,padding:'6px 10px',cursor:'pointer',color:C.muted,fontSize:18}}><i className="ti ti-x"/></button>
            </div>
            <div style={{padding:24}}>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:16}}>
                {[{label:'Sent',value:viewCampaign.sent_count||0,color:C.primary},{label:'Opened',value:viewCampaign.opened_count||0,color:C.warning},{label:'Clicked',value:viewCampaign.clicked_count||0,color:'#8B5CF6'},{label:'Bounced',value:viewCampaign.bounced_count||0,color:C.danger}].map(s=>(
                  <div key={s.label} style={{background:'#f9fafb',borderRadius:10,padding:'12px 14px',textAlign:'center',border:`1px solid ${C.border}`}}>
                    <div style={{fontSize:22,fontWeight:700,color:s.color}}>{s.value}</div>
                    <div style={{fontSize:11,color:C.muted}}>{s.label}</div>
                    {(viewCampaign.sent_count||0)>0&&<div style={{fontSize:10,color:C.faint}}>{pct(s.value,viewCampaign.sent_count||1)}%</div>}
                  </div>
                ))}
              </div>
              {(viewCampaign.sent_count||0)>0&&(
                <div style={{background:'#f9fafb',borderRadius:10,padding:'14px 16px',marginBottom:16}}>
                  <div style={{fontWeight:600,fontSize:12,color:C.muted,marginBottom:12,letterSpacing:0.5}}>PERFORMANCE RATES</div>
                  <StatBar label="Open Rate"        value={viewCampaign.opened_count||0}      total={viewCampaign.sent_count||0} color={C.warning}/>
                  <StatBar label="Click Rate"       value={viewCampaign.clicked_count||0}     total={viewCampaign.sent_count||0} color="#8B5CF6"/>
                  <StatBar label="Bounce Rate"      value={viewCampaign.bounced_count||0}     total={viewCampaign.sent_count||0} color={C.danger}/>
                  <StatBar label="Unsubscribe Rate" value={viewCampaign.unsubscribe_count||0} total={viewCampaign.sent_count||0} color="#f59e0b"/>
                </div>
              )}
              <div style={{marginBottom:16}}>
                <div style={{fontSize:12,fontWeight:600,color:C.muted,marginBottom:8,letterSpacing:0.5}}>EMAIL PREVIEW</div>
                <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:'hidden'}}>
                  <div style={{background:'#f9fafb',padding:'8px 14px',borderBottom:`1px solid ${C.border}`,fontSize:11,color:C.muted}}>Subject: {viewCampaign.subject}</div>
                  <iframe srcDoc={viewCampaign.body_html||'<em>No content</em>'} title="Email preview" style={{width:'100%',height:280,border:'none',display:'block'}} sandbox="allow-same-origin"/>
                </div>
              </div>
              <div>
                <div style={{fontSize:12,fontWeight:600,color:C.muted,marginBottom:8,letterSpacing:0.5}}>RECIPIENTS ({loadingDetail?'loading...':viewCampaign.recipients?.length||0})</div>
                {loadingDetail?(<div style={{padding:20,display:'flex',flexDirection:'column',gap:8}}>{Array(3).fill(null).map((_,i)=><Skeleton key={i} height={36} radius={6}/>)}</div>):
                 !viewCampaign.recipients?.length?(<div style={{textAlign:'center',padding:20,color:C.faint,fontSize:13}}>No recipient data yet.</div>):(
                  <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:'hidden',maxHeight:240,overflowY:'auto'}}>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                      <thead><tr style={{background:'#f9fafb',borderBottom:`1px solid ${C.border}`}}>
                        {['Name','Email','Opened','Clicked','Unsub','Sent At'].map(h=><th key={h} style={{padding:'8px 12px',textAlign:'left',fontWeight:500,color:C.muted}}>{h}</th>)}
                      </tr></thead>
                      <tbody>{viewCampaign.recipients.map((r,i)=>(
                        <tr key={r.id||i} style={{borderBottom:'1px solid #f3f4f6',background:r.unsubscribed?'#fef2f2':'transparent'}}>
                          <td style={{padding:'8px 12px'}}><div style={{display:'flex',alignItems:'center',gap:7}}><div style={{width:24,height:24,borderRadius:'50%',background:C.primary,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:9,fontWeight:700,flexShrink:0}}>{initials(r.lead?.name||'?')}</div>{r.lead?.name||'—'}</div></td>
                          <td style={{padding:'8px 12px',color:C.muted}}>{r.lead?.email||'—'}</td>
                          <td style={{padding:'8px 12px',textAlign:'center'}}>{r.opened?<i className="ti ti-circle-check" style={{color:C.success,fontSize:15}}/>:<i className="ti ti-circle" style={{color:'#e5e7eb',fontSize:15}}/>}</td>
                          <td style={{padding:'8px 12px',textAlign:'center'}}>{r.clicked?<i className="ti ti-circle-check" style={{color:'#8B5CF6',fontSize:15}}/>:<i className="ti ti-circle" style={{color:'#e5e7eb',fontSize:15}}/>}</td>
                          <td style={{padding:'8px 12px',textAlign:'center'}}>{r.unsubscribed?<i className="ti ti-circle-check" style={{color:C.danger,fontSize:15}}/>:<i className="ti ti-circle" style={{color:'#e5e7eb',fontSize:15}}/>}</td>
                          <td style={{padding:'8px 12px',color:C.faint,fontSize:11}}>{r.sent_at?new Date(r.sent_at).toLocaleString('en-IN',{dateStyle:'short',timeStyle:'short'}):'—'}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{marginBottom:24}}>
        <h1 style={{fontSize:20,fontWeight:700,color:C.dark,marginBottom:2}}>Email System</h1>
        <p style={{fontSize:13,color:C.muted}}>Configure sending domains, manage email accounts, build campaigns</p>
      </div>

      {/* Main tabs */}
      <div style={{display:'flex',gap:4,marginBottom:24,background:'#fff',borderRadius:12,border:`1px solid ${C.border}`,padding:6}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setActiveTab(t.id)}
            style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:6,padding:'9px 12px',borderRadius:8,border:'none',background:activeTab===t.id?C.primary:'transparent',color:activeTab===t.id?'#fff':C.muted,fontSize:12,fontWeight:activeTab===t.id?600:400,cursor:'pointer'}}>
            <i className={`ti ${t.icon}`} style={{fontSize:15}}/>{t.label}
          </button>
        ))}
      </div>

      {/* ── SETUP TAB ── */}
      {activeTab==='setup'&&(
        <div>
          <div style={{display:'flex',gap:4,marginBottom:20,background:'#f9fafb',borderRadius:10,border:`1px solid ${C.border}`,padding:5,width:'fit-content'}}>
            {SETUP_SECTIONS.map(s=>(
              <button key={s.id} onClick={()=>setSetupSection(s.id)}
                style={{display:'flex',alignItems:'center',gap:7,padding:'8px 18px',borderRadius:7,border:'none',background:setupSection===s.id?'#fff':'transparent',color:setupSection===s.id?C.primary:C.muted,fontSize:13,fontWeight:setupSection===s.id?600:400,cursor:'pointer',boxShadow:setupSection===s.id?'0 1px 4px rgba(0,0,0,0.08)':'none'}}>
                <i className={`ti ${s.icon}`} style={{fontSize:15}}/>{s.label}
                {s.id==='domains'&&domains.length>0&&<span style={{background:C.primary,color:'#fff',borderRadius:20,padding:'1px 7px',fontSize:10}}>{domains.length}</span>}
                {s.id==='accounts'&&providers.length>0&&<span style={{background:C.primary,color:'#fff',borderRadius:20,padding:'1px 7px',fontSize:10}}>{providers.length}</span>}
              </button>
            ))}
          </div>

          {setupSection==='domains'&&(
            <div>
              <div style={{background:'#f0f9ff',border:'1px solid #bae6fd',borderRadius:10,padding:'12px 16px',marginBottom:16,fontSize:13,color:'#0369a1',display:'flex',gap:10}}>
                <i className="ti ti-info-circle" style={{fontSize:17,flexShrink:0,marginTop:1}}/>
                <div><strong>Important:</strong> Your SMTP From Email domain must match the sending domain. e.g. domain = <code>mail.yourdomain.com</code>, from email = <code>you@mail.yourdomain.com</code>. DNS propagation takes 24–48 hours.</div>
              </div>
              <div style={{display:'flex',justifyContent:'flex-end',marginBottom:12}}>
                <Btn onClick={()=>setShowAddDomainModal(true)}><i className="ti ti-plus"/> Add Sending Domain</Btn>
              </div>
              {domainsLoading?Array(2).fill(null).map((_,i)=><DomainSkeleton key={i}/>):
               domains.length===0?(
                <div style={{background:'#fff',borderRadius:12,border:`2px dashed ${C.border}`,padding:40,textAlign:'center'}}>
                  <i className="ti ti-world" style={{fontSize:36,color:'#d1d5db',display:'block',marginBottom:10}}/>
                  <div style={{fontWeight:600,fontSize:14,color:C.dark,marginBottom:8}}>No sending domains yet</div>
                  <Btn onClick={()=>setShowAddDomainModal(true)}><i className="ti ti-plus"/> Add Domain</Btn>
                </div>
              ):domains.map(dom=>(
                <div key={dom.id} style={{background:'#fff',borderRadius:12,border:`1px solid ${domainStatusColor(dom.status)}40`,marginBottom:12,overflow:'hidden'}}>
                  <div style={{display:'flex',alignItems:'center',gap:12,padding:'14px 18px',cursor:'pointer'}} onClick={()=>setExpandedDomain(expandedDomain===dom.id?null:dom.id)}>
                    <i className="ti ti-world" style={{fontSize:20,color:C.primary,flexShrink:0}}/>
                    <div style={{flex:1}}><div style={{fontWeight:700,fontSize:14,color:C.dark}}>{dom.domain}</div><div style={{fontSize:11,color:C.muted}}>Added {new Date(dom.created_at).toLocaleDateString()}</div></div>
                    <span style={{background:`${domainStatusColor(dom.status)}18`,color:domainStatusColor(dom.status),borderRadius:20,padding:'3px 12px',fontSize:12,fontWeight:600}}>{domainStatusLabel(dom.status)}</span>
                    <div style={{display:'flex',gap:4}}>{(dom.dns_records||[]).map(r=><span key={r.label} style={{fontSize:10,padding:'2px 6px',borderRadius:4,background:r.status==='verified'?'#f0fdf4':r.status==='failed'?'#fef2f2':'#f9fafb',color:r.status==='verified'?C.success:r.status==='failed'?C.danger:C.faint,fontWeight:600}}>{r.label}</span>)}</div>
                    <i className={`ti ti-chevron-${expandedDomain===dom.id?'up':'down'}`} style={{fontSize:14,color:C.faint}}/>
                  </div>
                  {expandedDomain===dom.id&&(
                    <div style={{padding:'16px 18px',borderTop:`1px solid ${C.border}`}}>
                      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
                        <div style={{fontSize:13,color:C.muted}}>Add these DNS records, then click Check DNS.</div>
                        <div style={{display:'flex',gap:8}}>
                          <Btn size="sm" onClick={()=>handleVerifyDomain(dom.id)} disabled={verifying===dom.id}><i className="ti ti-refresh"/>{verifying===dom.id?'Checking...':'Check DNS Records'}</Btn>
                          <button onClick={()=>handleDeleteDomain(dom.id)} style={{padding:'5px 10px',borderRadius:6,border:'none',background:'#FEF2F2',color:C.danger,cursor:'pointer',fontSize:12}}><i className="ti ti-trash"/></button>
                        </div>
                      </div>
                      {(dom.dns_records||[]).map((r,i)=><RecordRow key={i} r={r} copied={copied} onCopy={handleCopy}/>)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {setupSection==='accounts'&&(
            <div>
              <div style={{display:'flex',justifyContent:'flex-end',marginBottom:12}}>
                <Btn onClick={()=>setShowAddAccountModal(true)}><i className="ti ti-plus"/> Add Email Account</Btn>
              </div>
              {providersLoading?Array(2).fill(null).map((_,i)=><ProviderSkeleton key={i}/>):
               providers.length===0?(
                <div style={{background:'#fff',borderRadius:12,border:`2px dashed ${C.border}`,padding:40,textAlign:'center'}}>
                  <i className="ti ti-mail-cog" style={{fontSize:36,color:'#d1d5db',display:'block',marginBottom:10}}/>
                  <div style={{fontWeight:600,fontSize:14,color:C.dark,marginBottom:8}}>No email accounts yet</div>
                  <Btn onClick={()=>setShowAddAccountModal(true)}><i className="ti ti-plus"/> Add Email Account</Btn>
                </div>
              ):providers.map(prov=>{
                const pInfo=PROVIDERS_META.find(p=>p.id===prov.provider);
                return (
                  <div key={prov.id} style={{background:'#fff',borderRadius:12,border:`1px solid ${prov.is_default?C.primary:C.border}`,padding:18,marginBottom:10}}>
                    <div style={{display:'flex',alignItems:'center',gap:14}}>
                      <i className={`ti ${pInfo?.icon||'ti-mail'}`} style={{fontSize:24,color:pInfo?.color||C.primary,flexShrink:0}}/>
                      <div style={{flex:1}}>
                        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:3}}>
                          <span style={{fontWeight:700,fontSize:14,color:C.dark}}>{prov.from_email||prov.username}</span>
                          {prov.is_default&&<span style={{background:'var(--color-primary-light)',color:C.primary,borderRadius:20,padding:'2px 10px',fontSize:11,fontWeight:600}}>Default</span>}
                        </div>
                        <div style={{fontSize:12,color:C.muted}}>{pInfo?.label||prov.provider}{prov.from_name&&` · ${prov.from_name}`}{prov.host&&` · ${prov.host}:${prov.port}`}</div>
                      </div>
                      <div style={{display:'flex',gap:8}}>
                        <Btn size="sm" variant="outline" onClick={()=>handleTest(prov.id)} disabled={testingId===prov.id}><i className="ti ti-mail-forward"/>{testingId===prov.id?'Sending...':'Test'}</Btn>
                        {!prov.is_default&&<Btn size="sm" variant="ghost" onClick={()=>handleSetDefault(prov.id)}>Set Default</Btn>}
                        <button onClick={()=>handleDeleteProvider(prov.id)} style={{padding:'5px 10px',borderRadius:6,border:'none',background:'#FEF2F2',color:C.danger,cursor:'pointer',fontSize:12}}><i className="ti ti-trash"/></button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {setupSection==='signature'&&(
            <Card title="Email Signature" icon="ti-writing">
              <p style={{fontSize:13,color:C.muted,marginBottom:16}}>
                Auto-appended to campaigns. Use <code>{'{{signature}}'}</code> in your email body to control placement.
              </p>
              {providers.length===0?(
                <div style={{background:'#fef3c7',border:'1px solid #fde68a',borderRadius:8,padding:'10px 14px',fontSize:13,color:'#92400e'}}>
                  <i className="ti ti-alert-triangle" style={{marginRight:6}}/>Please add an email account first.
                </div>
              ):(
                <>
                  <div style={{marginBottom:14}}>
                    <label style={{fontSize:12,fontWeight:500,color:C.text,marginBottom:4,display:'block'}}>Email Account</label>
                    <select style={inp} value={sigProviderId} onChange={e=>{ setSigProviderId(e.target.value); fetchSignature(e.target.value); }}>
                      {providers.map(p=><option key={p.id} value={p.id}>{p.from_email} {p.is_default?'(Default)':''}</option>)}
                    </select>
                    <div style={{fontSize:11,color:C.faint,marginTop:4}}>Each email account can have its own signature.</div>
                  </div>
                  {sigLoading?<div style={{marginBottom:16}}><Skeleton height={100} radius={8}/></div>:(
                    <div style={{marginBottom:14}}>
                      <label style={{fontSize:12,fontWeight:500,color:C.text,marginBottom:6,display:'block'}}>Signature</label>
                      <SigEditor value={signature} onChange={setSignature}/>
                    </div>
                  )}
                  <Btn onClick={handleSaveSignature} disabled={sigSaving}>
                    <i className={`ti ${sigSaving?'ti-loader ti-spin':'ti-device-floppy'}`}/>{sigSaving?'Saving...':'Save Signature'}
                  </Btn>
                  {signature&&(
                    <div style={{marginTop:16,background:'#f9fafb',borderRadius:8,border:`1px solid ${C.border}`,padding:16}}>
                      <div style={{fontSize:11,fontWeight:600,color:C.muted,marginBottom:8,letterSpacing:0.5}}>PREVIEW</div>
                      <div style={{borderTop:`1px solid #e5e7eb`,paddingTop:12,fontSize:13,color:'#6b7280'}} dangerouslySetInnerHTML={{__html:signature}}/>
                    </div>
                  )}
                </>
              )}
            </Card>
          )}
        </div>
      )}

      {/* ── CAMPAIGNS TAB ── */}
      {activeTab==='campaigns'&&(
        <div>
          <div style={{display:'flex',justifyContent:'flex-end',marginBottom:12}}>
            <Btn onClick={openCompose}><i className="ti ti-plus"/> New Campaign</Btn>
          </div>
          <Card title="All Campaigns" icon="ti-mail">
            {campaignsLoading?Array(4).fill(null).map((_,i)=><CampaignSkeleton key={i}/>):
             campaigns.length===0?(
              <div style={{textAlign:'center',padding:36,color:C.faint}}>
                <i className="ti ti-send" style={{fontSize:36,display:'block',marginBottom:10,color:'#d1d5db'}}/>
                <div style={{fontWeight:600,fontSize:14,color:C.dark,marginBottom:4}}>No campaigns yet</div>
                <div style={{fontSize:13,marginBottom:16}}>Click "New Campaign" to get started.</div>
                <Btn onClick={openCompose}><i className="ti ti-plus"/> New Campaign</Btn>
              </div>
            ):campaigns.map((c,i)=>(
              <div key={c.id} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 0',borderBottom:i<campaigns.length-1?'1px solid #f3f4f6':'none'}}>
                <div style={{width:36,height:36,borderRadius:8,background:'var(--color-primary-light)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <i className={`ti ${c.status==='scheduled'?'ti-calendar-time':'ti-mail'}`} style={{fontSize:18,color:C.primary}}/>
                </div>
                <div style={{flex:1,cursor:'pointer',minWidth:0}} onClick={()=>openCampaignDetail(c)}>
                  <div style={{fontWeight:500,fontSize:13}}>{c.name}</div>
                  <div style={{fontSize:11,color:C.muted,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {c.subject} · {new Date(c.created_at).toLocaleDateString()}
                    {c.status==='scheduled'&&c.scheduled_at&&<span style={{color:C.warning,marginLeft:6}}>· Scheduled {new Date(c.scheduled_at).toLocaleString('en-IN',{dateStyle:'short',timeStyle:'short'})}</span>}
                  </div>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:10,flexShrink:0}}>
                  {(c.sent_count||0)>0&&<div style={{textAlign:'right'}}>
                    <div style={{fontSize:12,fontWeight:600,color:C.dark}}>{c.sent_count} sent</div>
                    {(c.opened_count||0)>0&&<div style={{fontSize:11,color:C.muted}}>{pct(c.opened_count,c.sent_count)}% opened</div>}
                  </div>}
                  <span style={{background:c.status==='sent'?'#f0fdf4':c.status==='draft'?'#f9fafb':c.status==='scheduled'?'#fffbeb':'#eff6ff',color:c.status==='sent'?C.success:c.status==='draft'?C.muted:c.status==='scheduled'?C.warning:C.primary,borderRadius:20,padding:'2px 10px',fontSize:11,fontWeight:500}}>{c.status}</span>
                  <Btn size="sm" variant="ghost" onClick={()=>openCampaignDetail(c)}><i className="ti ti-eye"/></Btn>
                  {(c.status==='draft'||c.status==='scheduled')&&<Btn size="sm" onClick={()=>openEdit(c)}><i className="ti ti-edit"/></Btn>}
                  <button onClick={()=>handleDeleteCampaign(c.id)} style={{padding:'4px 8px',borderRadius:6,border:'none',background:'#FEF2F2',color:C.danger,cursor:'pointer',fontSize:12}}><i className="ti ti-trash"/></button>
                </div>
              </div>
            ))}
          </Card>
        </div>
      )}

      {/* ── TEMPLATES TAB ── */}
      {activeTab==='templates'&&(
        <div>
          <div style={{display:'flex',justifyContent:'flex-end',marginBottom:12}}>
            <Btn onClick={()=>setShowTemplate(true)}><i className="ti ti-plus"/> New Template</Btn>
          </div>
          {showTemplate&&(
            <Card title="New Email Template" icon="ti-file-text" style={{marginBottom:16}}>
              <div style={{display:'grid',gap:10,marginBottom:14}}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
                  <Input label="Name"     placeholder="e.g. Follow-up"  value={newTemplate.name}     onChange={e=>setNewTemplate(t=>({...t,name:e.target.value}))}/>
                  <Input label="Subject"  placeholder="Subject line..."  value={newTemplate.subject}  onChange={e=>setNewTemplate(t=>({...t,subject:e.target.value}))}/>
                  <Input label="Category" placeholder="e.g. follow-up"  value={newTemplate.category} onChange={e=>setNewTemplate(t=>({...t,category:e.target.value}))}/>
                </div>
                <div>
                  <label style={{fontSize:12,fontWeight:500,color:C.text,marginBottom:6,display:'block'}}>Body</label>
                  <textarea rows={6} placeholder="Email body (HTML supported)" value={newTemplate.body_html}
                    onChange={e=>setNewTemplate(t=>({...t,body_html:e.target.value}))}
                    style={{width:'100%',padding:'10px 12px',borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,outline:'none',resize:'vertical',fontFamily:'monospace',boxSizing:'border-box'}}/>
                </div>
              </div>
              <div style={{display:'flex',gap:8}}>
                <Btn onClick={handleSaveTemplate}><i className="ti ti-device-floppy"/> Save</Btn>
                <Btn variant="outline" onClick={()=>setShowTemplate(false)}>Cancel</Btn>
              </div>
            </Card>
          )}
          <div style={{marginBottom:20}}>
            <div style={{fontSize:13,fontWeight:600,color:C.dark,marginBottom:10}}>⚡ Pre-built Templates</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              {DEFAULT_TEMPLATES.map((t,i)=>(
                <div key={i} style={{background:'#fff',borderRadius:10,border:`1px solid ${C.border}`,padding:16}}>
                  <div style={{fontWeight:600,fontSize:13,marginBottom:4}}>{t.name}</div>
                  <div style={{fontSize:12,color:C.muted,marginBottom:10}}>{t.subject}</div>
                  <div style={{display:'flex',gap:8,alignItems:'center'}}>
                    <Btn size="sm" onClick={()=>handleUseTemplate(t)}><i className="ti ti-arrow-right"/> Use in Campaign</Btn>
                    <span style={{background:'var(--color-primary-light)',color:C.primary,borderRadius:20,padding:'3px 10px',fontSize:11}}>{t.category}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <Card title="Saved Templates" icon="ti-file-text">
            {templatesLoading?Array(2).fill(null).map((_,i)=><div key={i} style={{padding:'12px 0',borderBottom:'1px solid #f3f4f6'}}><Skeleton height={50} radius={8}/></div>):
             templates.length===0?<div style={{textAlign:'center',padding:24,color:C.faint,fontSize:13}}>No saved templates yet.</div>:
             templates.map((t,i)=>(
              <div key={t.id} style={{borderBottom:i<templates.length-1?'1px solid #f3f4f6':'none'}}>
                {editTemplate?.id===t.id?(
                  <div style={{padding:'12px 0'}}>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:10}}>
                      <Input label="Name"     value={editTemplate.name}         onChange={e=>setEditTemplate(x=>({...x,name:e.target.value}))}/>
                      <Input label="Subject"  value={editTemplate.subject||''}  onChange={e=>setEditTemplate(x=>({...x,subject:e.target.value}))}/>
                      <Input label="Category" value={editTemplate.category||''} onChange={e=>setEditTemplate(x=>({...x,category:e.target.value}))}/>
                    </div>
                    <textarea rows={5} value={editTemplate.body_html||''} onChange={e=>setEditTemplate(x=>({...x,body_html:e.target.value}))}
                      style={{width:'100%',padding:'10px 12px',borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,outline:'none',resize:'vertical',fontFamily:'monospace',boxSizing:'border-box',marginBottom:10}}/>
                    <div style={{display:'flex',gap:8}}>
                      <Btn size="sm" onClick={handleUpdateTemplate}><i className="ti ti-device-floppy"/> Save</Btn>
                      <Btn size="sm" variant="outline" onClick={()=>setEditTemplate(null)}>Cancel</Btn>
                    </div>
                  </div>
                ):(
                  <div style={{display:'flex',alignItems:'center',gap:12,padding:'12px 0'}}>
                    <div style={{width:36,height:36,borderRadius:8,background:'var(--color-primary-light)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><i className="ti ti-file-text" style={{fontSize:18,color:C.primary}}/></div>
                    <div style={{flex:1}}><div style={{fontWeight:500,fontSize:13}}>{t.name}</div><div style={{fontSize:11,color:C.muted}}>{t.subject}{t.category?` · ${t.category}`:''}</div></div>
                    <div style={{display:'flex',gap:6}}>
                      <Btn size="sm" onClick={()=>handleUseTemplate(t)}><i className="ti ti-arrow-right"/> Use</Btn>
                      <Btn size="sm" variant="ghost" onClick={()=>setEditTemplate({...t})}><i className="ti ti-edit"/></Btn>
                      <button onClick={()=>handleDeleteTemplate(t.id)} style={{padding:'5px 8px',borderRadius:6,border:'none',background:'#FEF2F2',color:C.danger,cursor:'pointer',fontSize:12}}><i className="ti ti-trash"/></button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </Card>
        </div>
      )}

      {/* ── ANALYTICS TAB ── */}
      {activeTab==='analytics'&&(
        <div>
          {analyticsLoading?(
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:20}}>
              {Array(4).fill(null).map((_,i)=>(
                <div key={i} style={{background:'#fff',borderRadius:12,border:`1px solid ${C.border}`,padding:'16px 18px'}}>
                  <Skeleton circle size={36} style={{marginBottom:10}}/><Skeleton height={24} width="40%" style={{marginBottom:6}}/><Skeleton height={12} width="60%"/>
                </div>
              ))}
            </div>
          ):analytics.length===0?(
            <div style={{background:'#fff',borderRadius:12,border:`2px dashed ${C.border}`,padding:48,textAlign:'center'}}>
              <i className="ti ti-chart-bar" style={{fontSize:40,color:'#d1d5db',display:'block',marginBottom:12}}/>
              <div style={{fontWeight:600,fontSize:15,color:C.dark,marginBottom:6}}>No analytics yet</div>
              <div style={{fontSize:13,color:C.faint}}>Send your first campaign to start tracking.</div>
            </div>
          ):(
            <>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:16}}>
                {[
                  {label:'Total Campaigns',value:analytics.length,                                    icon:'ti-mail',    color:C.primary},
                  {label:'Total Sent',      value:analytics.reduce((a,c)=>a+(c.sent_count||0),0),     icon:'ti-send',    color:C.success},
                  {label:'Total Opens',     value:analytics.reduce((a,c)=>a+(c.opened_count||0),0),   icon:'ti-eye',     color:C.warning},
                  {label:'Total Clicks',    value:analytics.reduce((a,c)=>a+(c.clicked_count||0),0),  icon:'ti-pointer', color:'#8B5CF6'},
                ].map(s=>(
                  <div key={s.label} style={{background:'#fff',borderRadius:12,border:`1px solid ${C.border}`,padding:'16px 18px'}}>
                    <div style={{width:36,height:36,borderRadius:8,background:`${s.color}18`,display:'flex',alignItems:'center',justifyContent:'center',marginBottom:10}}><i className={`ti ${s.icon}`} style={{fontSize:18,color:s.color}}/></div>
                    <div style={{fontSize:24,fontWeight:700,color:C.dark}}>{s.value.toLocaleString()}</div>
                    <div style={{fontSize:12,color:C.muted}}>{s.label}</div>
                  </div>
                ))}
              </div>
              {(()=>{
                const ts=analytics.reduce((a,c)=>a+(c.sent_count||0),0);
                const to=analytics.reduce((a,c)=>a+(c.opened_count||0),0);
                const tc=analytics.reduce((a,c)=>a+(c.clicked_count||0),0);
                const tb=analytics.reduce((a,c)=>a+(c.bounced_count||0),0);
                const tu=analytics.reduce((a,c)=>a+(c.unsubscribe_count||0),0);
                return ts>0?(
                  <div style={{background:'#fff',borderRadius:12,border:`1px solid ${C.border}`,padding:'16px 20px',marginBottom:16}}>
                    <div style={{fontWeight:600,fontSize:13,color:C.dark,marginBottom:14}}>Average Performance</div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
                      <div><StatBar label="Open Rate"   value={to} total={ts} color={C.warning}/><StatBar label="Click Rate"  value={tc} total={ts} color="#8B5CF6"/></div>
                      <div><StatBar label="Bounce Rate" value={tb} total={ts} color={C.danger}/><StatBar label="Unsubscribe Rate" value={tu} total={ts} color="#f59e0b"/></div>
                    </div>
                  </div>
                ):null;
              })()}
              <Card title="Campaign Performance" icon="ti-chart-line">
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                    <thead><tr style={{borderBottom:`1px solid ${C.border}`,background:'#f9fafb'}}>
                      {['Campaign','Sent','Opened','Clicked','Bounced','Unsub','Open Rate',''].map(h=><th key={h} style={{padding:'10px 14px',textAlign:'left',color:C.muted,fontWeight:500,fontSize:12}}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {analytics.map(c=>(
                        <tr key={c.id} style={{borderBottom:'1px solid #f3f4f6'}}>
                          <td style={{padding:'10px 14px'}}>
                            <div style={{fontWeight:500}}>{c.name}</div>
                            <div style={{fontSize:11,color:C.muted}}>{c.sent_at?new Date(c.sent_at).toLocaleDateString():new Date(c.created_at).toLocaleDateString()}</div>
                          </td>
                          <td style={{padding:'10px 14px',fontWeight:600}}>{c.sent_count||0}</td>
                          <td style={{padding:'10px 14px',color:C.warning}}>{c.opened_count||0}</td>
                          <td style={{padding:'10px 14px',color:'#8B5CF6'}}>{c.clicked_count||0}</td>
                          <td style={{padding:'10px 14px',color:C.danger}}>{c.bounced_count||0}</td>
                          <td style={{padding:'10px 14px',color:'#f59e0b'}}>{c.unsubscribe_count||0}</td>
                          <td style={{padding:'10px 14px'}}>
                            <div style={{display:'flex',alignItems:'center',gap:8}}>
                              <span style={{fontWeight:600,color:C.dark}}>{pct(c.opened_count||0,c.sent_count||0)}%</span>
                              <div style={{background:'#f3f4f6',borderRadius:4,height:6,width:60}}><div style={{width:`${pct(c.opened_count||0,c.sent_count||0)}%`,height:6,borderRadius:4,background:C.warning}}/></div>
                            </div>
                          </td>
                          <td style={{padding:'10px 14px'}}><Btn size="sm" variant="ghost" onClick={()=>openCampaignDetail(c)}><i className="ti ti-eye"/></Btn></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  );
}
